import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe, type SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { PrismIcon, PrismToggle } from '@mylighthouse/prism-ember';
import { ILLUSTRATIONS_MAP } from '@mylighthouse/prism-foundation';

interface SettingsRecommendationsSnowflakesRecommendationsSettingCardSignature {
  Args: {
    description: string;
    handleToggleChange?: () => void;
    hasToggle?: boolean;
    illustration: keyof typeof ILLUSTRATIONS_MAP;
    isToggleDisabled?: boolean;
    isToggled?: boolean;
    routeName?: string;
    title: string;
  };
  Blocks: {
    content?: [];
  };
}

export default class SettingsRecommendationsSnowflakesRecommendationsSettingCard extends Component<SettingsRecommendationsSnowflakesRecommendationsSettingCardSignature> {
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasAction() {
    return this.args.hasToggle || this.args.routeName;
  }

  get illustrationHTML(): SafeString {
    const illustration = ILLUSTRATIONS_MAP[this.args.illustration];
    return htmlSafe(illustration);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showYieldedContent() {
    return this.args.isToggled || !this.hasAction;
  }

  @action onCardClick(): void {
    if (this.args.routeName) {
      this.router.transitionTo(this.args.routeName);
    }
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="recommendations-setting-card pt-600 pr-400 pb-600 pl-1000
        {{if @routeName 'recommendations-setting-card--hoverable'}}"
      {{on "click" this.onCardClick}}
    >
      <div class="flex flex-row items-center gap-1000">
        <div class="flex items-center justify-center">
          {{this.illustrationHTML}}
        </div>
        <div class="flex w-full flex-row items-center justify-between">
          <div class="recommendations-setting-card__title-container">
            <h3>{{@title}}</h3>
            <p class="paragraph-300--regular">{{@description}}</p>
          </div>
          {{#if @hasToggle}}
            <div class="recommendations-setting-card__toggle">
              <PrismToggle
                @isChecked={{@isToggled}}
                @isDisabled={{@isToggleDisabled}}
                @onChange={{@handleToggleChange}}
              />
            </div>
          {{else if @routeName}}
            <PrismIcon @name="chevron-right" />
          {{/if}}
        </div>
      </div>
      {{#if (has-block "content")}}
        {{#if this.showYieldedContent}}
          <div class="recommendations-setting-card__toggle-content pt-300">
            {{yield to="content"}}
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
